import { PortalFilters } from "@/components/communications-chat-components/portal/PortalChatContext";
import { Conversation, StatisticsBody } from "@/components/communications-chat-components/portal/PortalInboxStatistics";
import { MainDashBoardChart } from "@/enums/MainDashBoardChart";
import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import { IPage } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/IPage";
import { IPageV2 } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/IPageV2";
import PagedResult from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/PagedResult";
import Tenant from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/Tenant";
import GenericCrudServiceV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/GenericCrudServiceV2";
import HttpClientV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import { LazoFile } from "@/models/LazoFile";
import { LazoUser } from "@/models/LazoUser";
import { FinancialStatementItem } from "@/models/accounting/ReportItem";
import { AVAILABLE_CONVERSATION_FILTERS, LazoChatConversation, LazoChatConversationShell } from "@/models/ai-chat/LazoChatConversation";
import { SharedFinancialsConversationCreation } from "@/models/ai-chat/SharedFinancialsConversationCreation";
import { SmallConversationMember } from "@/models/ai-chat/SmallConversationMember";
import AuditableEntity from "@/types/AuditableEntity";
import { ConversationType } from "@/types/ConversationType";
import { Funder } from "@/types/FunderTypes";
import { InboxActivityData } from "@/types/InboxActivityData";

export type LazoOneChatFilters = {
  searchValue: string;
  dateFilter: ChatDateFilter;
  activityFilter: ChatActivityFilter;
};

export enum ChatActivityFilter {
  ALL = "ALL",
  UNREADS_ONLY = "UNREADS_ONLY",
  MENTIONS_ONLY = "MENTIONS_ONLY",
}

export enum ChatDateFilter {
  YESTERDAY = "YESTERDAY",
  LAST_WEEK = "LAST_WEEK",
  LAST_MONTH = "LAST_MONTH",
}

class LazoChatConversationService extends GenericCrudServiceV2<LazoChatConversation> {
  path: string;
  constructor(path: string) {
    super(path);
    this.path = path;
  }

  getAllOrderByCreatedAtDesc(): Promise<ApiResponseV2<LazoChatConversation[]>> {
    return HttpClientV2.get(`${this.path}/all`);
  }

  getConversationsByExternalId(
    userExternalId: string,
    conversationType: ConversationType,
    tenantExternalId: string
  ): Promise<ApiResponseV2<LazoChatConversation[]>> {
    return HttpClientV2.get(
      `${this.path}/conversation-by-user?externalId=${userExternalId}&conversationType=${conversationType}&tenantExternalId=${tenantExternalId}`
    );
  }

  createChatWithKam(tenantExternalId: string): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.post(`${this.path}/with-kam?tenantExternalId=${tenantExternalId}`, {});
  }

  createPrivateChat(coreUserExternalId: string, tenantExternalId: string, message?: string): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.post(
      `${this.path}/create-private-chat?coreUserExternalId=${coreUserExternalId}&tenantExternalId=${tenantExternalId}`,
      { message: message || null }
    );
  }

  createPrivateGroupChat(
    usersExternalIds: string[],
    groupName: string | null,
    externalId: string,
    message?: string
  ): Promise<ApiResponseV2<LazoChatConversation>> {
    let queryStr = `?externalId=${externalId}`;
    if (groupName !== null) {
      queryStr += `&groupName=${groupName}`;
    }

    return HttpClientV2.post(`${this.path}/create-private-group-chat${queryStr}`, {
      usersExternalIds: usersExternalIds,
      message: message || null,
    });
  }

  getInternalChat(externalId: string): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.get(`${this.path}/internal-conversation?conversationExternalId=${externalId}`);
  }

  addTaggedMembers(userExternalId: string[], conversationExternalId: string): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.post(`${this.path}/add-inbox-chat-members?conversationExternalId=${conversationExternalId}`, userExternalId);
  }

  getAndConsumeMessages(conversationExternalId: string): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.get(`${this.path}/conversation?conversationExternalId=${conversationExternalId}`);
  }

  getChatMembers(conversationExternalId: string): Promise<ApiResponseV2<SmallConversationMember[]>> {
    return HttpClientV2.get(`${this.path}/chat-members/${conversationExternalId}`);
  }

  createNewConversation(
    title: string,
    tenant: Tenant,
    conversationType?: ConversationType,
    signal?: AbortSignal,
    contextEntityExternalId?: string
  ): Promise<ApiResponseV2<LazoChatConversation>> {
    // We add timeout because chatGPT takes too long to respond, and even if the backend doesn't timeout, the frontend does
    return HttpClientV2.post(
      `${this.path}`,
      {
        title: title,
        conversationType: conversationType,
        tenant,
        contextEntityExternalId,
      },
      { axiosConfig: { timeout: 1800000, signal } }
    );
  }

  createNewConversationChart(
    title: string,
    tenantExternalId: string,
    type: MainDashBoardChart,
    signal?: AbortSignal
  ): Promise<ApiResponseV2<LazoChatConversation>> {
    // We add timeout because chatGPT takes too long to respond, and even if the backend doesn't timeout, the frontend does
    return HttpClientV2.post(
      `${this.path}/chart/${type}`,
      { title: title, tenant: { externalId: tenantExternalId } },
      { axiosConfig: { timeout: 1800000, signal } }
    );
  }

  createNewConversationStatementTableItem(
    item: FinancialStatementItem & { chatTitle: string } & {
      tenant: { externalId: string };
    },
    signal?: AbortSignal
  ): Promise<ApiResponseV2<LazoChatConversation>> {
    // We add timeout because chatGPT takes too long to respond, and even if the backend doesn't timeout, the frontend does
    return HttpClientV2.post(`${this.path}/statement-table/item`, item, {
      axiosConfig: { timeout: 1800000, signal },
    });
  }

  createNewConversationFunder(
    funder: {
      funder: Funder;
      chatTitle: string;
      tenant: { externalId: string };
    },
    signal?: AbortSignal
  ): Promise<ApiResponseV2<LazoChatConversation>> {
    // We add timeout because chatGPT takes too long to respond, and even if the backend doesn't timeout, the frontend does
    return HttpClientV2.post(`${this.path}/funder`, funder, {
      axiosConfig: { timeout: 1800000, signal },
    });
  }

  createSingleDocumentConversation(
    req: {
      title: string;
      document: AuditableEntity;
      tenantExternalId: string;
      conversationType: ConversationType;
      createInitialMessage?: boolean;
    },
    signal?: AbortSignal
  ): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.post(`${this.path}/data-room/document`, req, {
      axiosConfig: { timeout: 120000, signal },
    });
  }

  createSharedFinancialConversation(
    req: SharedFinancialsConversationCreation,
    signal?: AbortSignal
  ): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.post(`${this.path}/data-room/shared-financial`, req, {
      axiosConfig: { timeout: 120000, signal },
    });
  }

  createSharedFinancialsItemConversation(
    req: {
      createReq: SharedFinancialsConversationCreation;
      item: FinancialStatementItem;
    },
    signal?: AbortSignal
  ): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.post(`${this.path}/data-room/shared-financial/item`, req, {
      axiosConfig: { timeout: 60000, signal },
    });
  }

  search(term: string): Promise<ApiResponseV2<LazoChatConversation[]>> {
    return HttpClientV2.get(`${this.path}/query?term=${term}`);
  }

  manageUsersFromConversation(
    usersToAdd: LazoUser[],
    usersToRemove: LazoUser[],
    conversationExternalId: string
  ): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.post(`${this.path}/manage-users?conversationExternalId=${conversationExternalId}`, {
      usersToAdd: usersToAdd,
      usersToRemove: usersToRemove,
    });
  }

  leaveConversation(conversationExternalId: string): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.post(`${this.path}/leave-conversation/${conversationExternalId}`, {});
  }

  enrichConversation(conversationExternalId: string): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.get(`${this.path}/enrich-conversation?conversationExternalId=${conversationExternalId}`);
  }
  renameConversation(conversationExternalId: string, newTitle: string): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.patchWithPayload(`${this.path}/rename-conversation`, {
      conversationExternalId,
      newTitle,
    });
  }

  archiveConversation(conversationExternalId: string): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.patchWithoutPayload(`${this.path}/archive-conversation/${conversationExternalId}`);
  }

  deleteConversation(conversationExternalId: string): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.delete(`${this.path}/delete-conversation/${conversationExternalId}`);
  }

  undoDeleteConversation(conversationExternalId: string): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.patchWithoutPayload(`${this.path}/undo-delete-conversation/${conversationExternalId}`);
  }

  addMentionsToConversationMembers(mentions: { id: string }[], conversationExternalId: string): Promise<ApiResponseV2<String>> {
    return HttpClientV2.post(`${this.path}/mentions?conversationExternalId=${conversationExternalId}`, mentions);
  }

  readConversation(conversationExternalId: string) {
    return HttpClientV2.patchWithoutPayload(`${this.path}/read/${conversationExternalId}`);
  }

  getConversationFiles(conversationExternalId: string): Promise<ApiResponseV2<LazoFile[]>> {
    const params = new URLSearchParams({
      conversationExternalId,
    });
    return HttpClientV2.get(`${this.path}/conversation-files`, {
      axiosConfig: {
        timeout: 45000,
        params,
      },
    });
  }

  getArchivedByUser(): Promise<ApiResponseV2<LazoChatConversation[]>> {
    return HttpClientV2.get(`${this.path}/archived`);
  }

  getByType(convType: ConversationType, tenantId: string): Promise<ApiResponseV2<LazoChatConversation[]>> {
    return HttpClientV2.get(`${this.path}/type?conversationType=${convType}&tenantId=${tenantId}`);
  }

  getByTypePaged(
    convType: ConversationType,
    tenantId: string,
    page: number,
    pageSize: number,
    contextEntityExternalId?: string
  ): Promise<ApiResponseV2<IPageV2<LazoChatConversation>>> {
    let qryStr = `?conversationType=${convType}&tenantId=${tenantId}&page=${page}&pageSize=${pageSize}`;
    if (contextEntityExternalId) {
      qryStr += `&contextEntityExternalId=${contextEntityExternalId}`;
    }
    return HttpClientV2.get(`${this.path}/type-paginated${qryStr}`);
  }

  //temporary solution
  getTenantNameByNoOwnerMember(conversationExternalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.get(`${this.path}/get-tenant-name-by-no-owner-member?conversationExternalId=${conversationExternalId}`);
  }

  getChatsByFilter(
    generalFilter: AVAILABLE_CONVERSATION_FILTERS,
    filters: PortalFilters,
    page: number
  ): Promise<ApiResponseV2<IPageV2<LazoChatConversationShell>>> {
    return HttpClientV2.post(
      `${this.path}/filter/get-inbox-chats-by-filter?filter=${generalFilter}&page=${page}`,
      {
        tenantExternalIds: filters.tenantExternalIds || [],
        searchValue: filters.searchValue,
        otherClients: filters.otherClients || false,
        employeesExternalIds: filters.employeesExternalIds || [],
        showResponseTimes: filters.showResponseTimes || false,
        status: filters.status || null,
        readStatus: filters.readStatus || null,
      },
      {
        axiosConfig: { timeout: 120000 },
      }
    );
  }

  /**
   *
   * @param userExternalId core user we want to find conversations with their KAMs
   * @param tenantExternalId
   */
  getConversationsWithKam(userExternalId: string, tenantExternalId: string): Promise<ApiResponseV2<LazoChatConversation[]>> {
    return HttpClientV2.get(`${this.path}/with-kam?userExternalId=${userExternalId}&tenantExternalId=${tenantExternalId}`);
  }

  getPrivateConversations(
    tenantExternalId: string,
    page: number,
    filters?: LazoOneChatFilters
  ): Promise<ApiResponseV2<IPageV2<LazoChatConversationShell>>> {
    return HttpClientV2.post(`${this.path}/chat/private?tenantExternalId=${tenantExternalId}&page=${page}`, {
      searchValue: filters?.["searchValue"] || null,
      activityFilter: filters?.["activityFilter"] || null,
      dateFilter: filters?.["dateFilter"] || null,
    });
  }

  getPrivateConversationsPaged(
    tenantExternalId: string,
    page: number,
    pageSize: number
  ): Promise<ApiResponseV2<PagedResult<LazoChatConversation>>> {
    return HttpClientV2.get(`${this.path}/chat/private-paged?tenantExternalId=${tenantExternalId}&page=${page}&pageSize=${pageSize}`);
  }

  manageUsers(
    usersToAddExternalIds: string[],
    usersToRemoveExternalIds: string[],
    conversationExternalId: string
  ): Promise<ApiResponseV2<LazoChatConversation>> {
    return HttpClientV2.post(`${this.path}/manage-users/inbox-chat?conversationExternalId=${conversationExternalId}`, {
      usersToAddExternalIds,
      usersToRemoveExternalIds,
    });
  }
  getInboxActivity(userExternalId: string, page: number = 0, pageSize: number = 5): Promise<ApiResponseV2<IPage<InboxActivityData>>> {
    return HttpClientV2.get(`${this.path}/inbox-activity?userExternalId=${userExternalId}&page=${page}&pageSize=${pageSize}`);
  }

  getCountChats(): Promise<ApiResponseV2<number>> {
    return HttpClientV2.get(`${this.path}/count-chat-unread`);
  }

  findConversationByMembers(members: string[], tenantExternalId?: string): Promise<ApiResponseV2<LazoChatConversation>> {
    let qry = ``;
    if (tenantExternalId) {
      qry += `?tenantExternalId=${tenantExternalId}`;
    }
    return HttpClientV2.post(`${this.path}/contains/members${qry}`, {
      values: members,
    });
  }

  finalizeThread(conversationExternalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.get(`${this.path}/thread/finalize?conversationExternalId=${conversationExternalId}`);
  }

  markAsUnread(conversationExternalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.get(`${this.path}/mark-as-unread?conversationExternalId=${conversationExternalId}`);
  }

  getStatistics(body: StatisticsBody, page: number): Promise<ApiResponseV2<IPageV2<Conversation>>> {
    return HttpClientV2.put(`${this.path}/filter/statistics?page=${page}`, body);
  }
}

export default new LazoChatConversationService("/api/lazo-chat-conversation");
